import { useState, useEffect, useContext } from "react";
import { AppContexts } from "../../../providers";

const SmartLocks = () => {
  const {
    settings,
    loading,
    syncLoading,
    error,
    syncResult,
    resSyncLoading,
    resSyncResult,
    timesSaving,
    timesSaved,
    setError,
    setSyncResult,
    setResSyncResult,
    loadSettings,
    updateSettings,
    syncSpaces,
    syncReservations,
    saveTimes,
  } = useContext(AppContexts.SeamContext);

  const [checkInTime, setCheckInTime] = useState("15:00");
  const [checkOutTime, setCheckOutTime] = useState("11:00");

  // ── Load settings on mount ──────────────────────────────────────────
  useEffect(() => {
    loadSettings().then((data) => {
      if (data) {
        setCheckInTime(data.standard_check_in_time || "15:00");
        setCheckOutTime(data.standard_check_out_time || "11:00");
      }
    });
  }, []);

  // ── Toggle Seam on/off ──────────────────────────────────────────────
  const handleToggle = async () => {
    try {
      const newEnabled = !settings.seam_enabled;
      await updateSettings({ seam_enabled: newEnabled });
      if (newEnabled) {
        syncSpaces();
      }
    } catch (err) {
      // error already set in provider
    }
  };

  // ── Toggle auto-generate codes ──────────────────────────────────────
  const handleAutoGenerateToggle = async () => {
    try {
      await updateSettings({
        seam_auto_generate_codes: !settings.seam_auto_generate_codes,
      });
    } catch (err) {
      // error already set in provider
    }
  };

  const handleSaveTimes = () => saveTimes(checkInTime, checkOutTime);

  // ── Render ──────────────────────────────────────────────────────────

  if (loading) {
    return (
      <li>
        <div className="uk-text-center uk-padding">
          <div uk-spinner="ratio: 1"></div>
          <p className="uk-text-muted">Loading smart lock settings...</p>
        </div>
      </li>
    );
  }

  return (
    <li>
      <h2>Smart Locks</h2>
      <div className="uk-card hms-left-tabs-content-card mx-w-665">
        <p className="secondary">
          Connect your smart locks through Seam to automatically generate and
          manage door codes for guest reservations.
        </p>

        {/* Error Alert */}
        {error && (
          <div className="uk-alert uk-alert-danger" uk-alert="">
            <a
              className="uk-alert-close"
              uk-close=""
              onClick={() => setError(null)}
            ></a>
            <p>{error}</p>
          </div>
        )}

        {/* Success Alert */}
        {syncResult && (
          <div className="uk-alert" style={{ backgroundColor: "#d4edda", color: "#155724", borderColor: "#c3e6cb" }} uk-alert="">
            <a
              className="uk-alert-close"
              uk-close=""
              onClick={() => setSyncResult(null)}
            ></a>
            <p>{syncResult}</p>
          </div>
        )}

        {/* Reservation Sync Result */}
        {resSyncResult && (
          <div className="uk-alert" style={{ backgroundColor: "#d4edda", color: "#155724", borderColor: "#c3e6cb" }} uk-alert="">
            <a
              className="uk-alert-close"
              uk-close=""
              onClick={() => setResSyncResult(null)}
            ></a>
            <p>{resSyncResult}</p>
          </div>
        )}

        {/* Enable/Disable Toggle */}
        <div className="uk-margin">
          <div className="uk-grid-small uk-flex-middle" uk-grid="">
            <div className="uk-width-auto">
              <div className="autosend-checkbox">
                <label className="setting-switch">
                  <input
                    type="checkbox"
                    checked={settings?.seam_enabled || false}
                    onChange={handleToggle}
                  />
                  <span className="setting-slider setting-round"></span>
                </label>
              </div>
            </div>
            <div className="uk-width-expand">
              {settings?.seam_enabled
                ? "Smart Locks Enabled"
                : "Enable Smart Locks"}
            </div>
          </div>
        </div>

        {settings?.seam_enabled && (
          <>
            {/* Auto-generate codes toggle */}
            <div className="uk-margin">
              <div className="uk-grid-small uk-flex-middle" uk-grid="">
                <div className="uk-width-auto">
                  <div className="autosend-checkbox">
                    <label className="setting-switch">
                      <input
                        type="checkbox"
                        checked={settings?.seam_auto_generate_codes || false}
                        onChange={handleAutoGenerateToggle}
                      />
                      <span className="setting-slider setting-round"></span>
                    </label>
                  </div>
                </div>
                <div className="uk-width-expand">
                  <span>Auto-generate door codes for new reservations</span>
                  <p className="secondary uk-margin-remove-top">
                    When enabled, Seam will automatically create time-bound
                    access codes for each reservation.
                  </p>
                </div>
              </div>
            </div>

            {/* Standard Check-in/Check-out Times */}
            <div className="uk-margin">
              <h4>Standard Check-in / Check-out Times</h4>
              <p className="secondary">
                Set your standard check-in and check-out times. These are used
                to schedule when access codes become active and expire.
              </p>
              <div className="uk-grid-small" uk-grid="">
                <div className="uk-width-1-4">
                  <label className="uk-form-label">Check-in Time</label>
                  <input
                    className="uk-input uk-form-small"
                    type="time"
                    value={checkInTime}
                    onChange={(e) => setCheckInTime(e.target.value)}
                  />
                </div>
                <div className="uk-width-1-4">
                  <label className="uk-form-label">Check-out Time</label>
                  <input
                    className="uk-input uk-form-small"
                    type="time"
                    value={checkOutTime}
                    onChange={(e) => setCheckOutTime(e.target.value)}
                  />
                </div>
                <div className="uk-width-expand uk-flex uk-flex-bottom">
                  <button
                    className="uk-button hms-btn hms-red-btn"
                    onClick={handleSaveTimes}
                    disabled={timesSaving}
                  >
                    {timesSaving ? (
                      <div data-uk-spinner="ratio: 0.5;"></div>
                    ) : timesSaved ? (
                      "Saved"
                    ) : (
                      "Save"
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Sync Properties Button */}
            <div className="uk-margin">
              <button
                className="uk-button hms-btn hms-red-btn"
                onClick={syncSpaces}
                disabled={syncLoading}
              >
                {syncLoading ? (
                  <>
                    <span uk-spinner="ratio: 0.5"></span> Syncing...
                  </>
                ) : (
                  "Sync Properties to Seam"
                )}
              </button>
              <div className="secondary">
                Push your property list to Seam so you can map locks to
                properties.
              </div>
            </div>

            {/* Sync Reservations Button */}
            <div className="uk-margin">
              <button
                className="uk-button hms-btn hms-red-btn"
                onClick={syncReservations}
                disabled={resSyncLoading}
              >
                {resSyncLoading ? (
                  <>
                    <span uk-spinner="ratio: 0.5"></span> Syncing Reservations...
                  </>
                ) : (
                  "Sync Reservations to Seam"
                )}
              </button>
              <div className="secondary">
                Push all upcoming reservations for properties with mapped locks
                to generate access codes.
              </div>
            </div>
          </>
        )}
      </div>
    </li>
  );
};

export default SmartLocks;
